import React, { useState, useEffect } from "react";
import { Link, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../images/logo.png";
import LanguageSwitcher from "../components/LanguageSwitcher";
import UserMenu from "../components/UserMenu";
import api from "../services/api";

type LayoutProps = {
  onLogout: () => void;
  children?: React.ReactNode;
};

function Layout({ onLogout, children }: LayoutProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState<{ first_name: string; last_name: string; package?: { has_chat?: boolean }; is_expired?: boolean } | null>(null);

  // Check if user is authenticated
  const token = localStorage.getItem("token");

  useEffect(() => {
    // Only fetch user if authenticated
    if (!token) return;

    const fetchUser = async () => {
      try {
        const res = await api.get('/user/profile/me');
        setUser(res.data);
      } catch (err) {
        console.error('Failed to fetch user', err);
        // If token is invalid, logout
        if ((err as any)?.response?.status === 401) {
          onLogout();
          navigate(`/${i18n.language}/login`);
        }
      }
    };
    fetchUser();
  }, [token, i18n.language, navigate, onLogout]);

  // Redirect to login if not authenticated
  if (!token) {
    return <Navigate to={`/${i18n.language}/login`} replace />;
  }

  const handleLogout = () => {
    onLogout();
    navigate(`/${i18n.language}/login`);
  };

  return (
    <div className="app-container d-flex flex-column h-100">
      {/* Navbar */}
      <nav className="navbar navbar-dark bg-primary fixed-top app-navbar">
        <div className="container-fluid d-flex justify-content-between align-items-center">
          <Link className="navbar-brand" to={`/${i18n.language}/chat`}>
            <img className="logo" src={logo} alt="Logo" />
          </Link>
          <h3 className="text-light d-none d-md-block">{t("layout.headerSlogan")}</h3>
          <div className="d-flex align-items-center gap-3">
            {/* Desktop Nav Items - Hidden on Mobile */}
            <div className="d-none d-md-flex align-items-center gap-3">
              {/* Language Switcher */}
              <LanguageSwitcher />
              {/* User Menu */}
              <UserMenu user={user} onLogout={handleLogout} variant="dark" />
            </div>

            {/* Always Visible Icons */}
            <Link to={`/${i18n.language}`} className="text-light text-decoration-none" title={t("nav.home", "Accueil")}>
              <i className="bi bi-house-door fs-4"></i>
            </Link>

            {user?.package?.has_chat !== false && (
              <Link to={`/${i18n.language}/chat`} className="text-light text-decoration-none" title={t("nav.chatbot", "Chatbot IA")}>
                <i className="bi bi-chat-dots fs-4"></i>
              </Link>
            )}

            {/* Logout Icon (Visible on Mobile) */}
            <button
              className="btn btn-link text-light text-decoration-none p-0 d-md-none"
              onClick={handleLogout}
              title={t("common.logout", "Déconnexion")}
            >
              <i className="bi bi-box-arrow-right fs-4"></i>
            </button>

            <button
              className="btn btn-outline-light d-md-none"
              onClick={() => setSidebarOpen((prev) => !prev)}
              title={t("layout.menu")}
            >
              <i className="bi bi-list fs-4"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Content area with sidebar + chat */}
      <div className="content-wrapper d-flex">
        {children ? children : <Outlet context={{ sidebarOpen, setSidebarOpen, handleLogout, user }} />}
      </div>
    </div>
  );
}

export default Layout;
